<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: DNAMatchNotification                                  |
| Section: components                                              |
| Purpose: Notification banner summarizing new DNA match updates.  |
+------------------------------------------------------------------+
-->
<template>
    <div v-if="isVisible" class="dna-match-notification">
        <div class="notification-content">
            <div class="match-header">
                <div class="match-heading">
                    <div class="match-badge">{{ $t('dnaMatch.badge', { category: matchLabel }) }}</div>
                    <div v-if="totalCount > 1" class="match-count">{{ queuePosition }} / {{ totalCount }}</div>
                </div>
                <button class="close-btn" @click="close" aria-label="Close notification">
                    ×
                </button>
            </div>

            <div class="match-info">
                <p class="match-person">{{ matchData.name }}</p>
                <p class="match-context">{{ matchData.dnaInfo.relation }}</p>

                <div class="dna-highlights">
                    <div class="highlight-pill">
                        <span class="pill-label">{{ $t('dnaMatch.sharedDNA') }}</span>
                        <span class="pill-value">{{ matchData.dnaInfo.sharedDNA }}</span>
                    </div>
                    <div class="highlight-pill">
                        <span class="pill-label">{{ $t('dnaMatch.segments') }}</span>
                        <span class="pill-value">{{ matchData.dnaInfo.sharedSegments }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DNAMatchNotification',
    props: {
        matchData: {
            type: Object,
            required: true,
            validator: (obj) => {
                return obj && obj.dnaInfo && obj.name && obj.dnaInfo.relation;
            }
        },
        queuePosition: {
            type: Number,
            default: 1
        },
        totalCount: {
            type: Number,
            default: 1
        }
    },
    data() {
        return {
            isVisible: false,
            showTimeout: null
        };
    },
    computed: {
        matchLabel() {
            const category = this.matchData.category;
            return `${category}`;
        }
    },
    methods: {
        close() {
            this.isVisible = false;
            this.$emit('closed');
            if (this.showTimeout) clearTimeout(this.showTimeout);
        }
    },
    mounted() {
        this.showTimeout = setTimeout(() => {
            this.isVisible = true;
        }, 150);
    },
    beforeUnmount() {
        if (this.showTimeout) clearTimeout(this.showTimeout);
    }
};
</script>

<style scoped>
.dna-match-notification {
    animation: slideIn 0.24s ease-out;
    max-width: 272px;
}

@keyframes slideIn {
    from {
        transform: translateY(12px);
        opacity: 0;
    }

    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.notification-content {
    background: rgba(29, 29, 29, .9);
    border: 1px solid rgba(29, 29, 29, 1);
    border-radius: 10px;
    padding: 12px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3), 0 0 1px rgba(52, 211, 153, 0.3);
    backdrop-filter: blur(10px);
}

.match-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.match-heading {
    display: flex;
    align-items: center;
    gap: 8px;
}

.match-badge {
    display: inline-block;
    background: var(--brand-color-1);
    color: var(--brand-bg-1);
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
}

.match-count {
    color: rgba(255, 255, 255, 0.72);
    font-size: 11px;
}

.close-btn {
    background: none;
    border: none;
    color: #fff;
    font-size: 20px;
    cursor: pointer;
    padding: 0;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s ease;
}

.close-btn:hover {
    color: #fff;
}

.match-info {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.match-person {
    margin: 0;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
}

.match-context {
    margin: 0;
    color: rgba(255, 255, 255, 0.8);
    font-size: 12px;
    line-height: 1.35;
}

.dna-highlights {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.highlight-pill {
    display: inline-flex;
    flex-direction: column;
    gap: 6px;
    padding: 6px 8px;
    min-width: 92px;
    border-radius: var(--border-radius-md);
    background: rgba(255, 255, 255, 0.05);
}

.pill-label {
    color: rgba(255, 255, 255, 0.68);
    font-size: 10px;
    line-height: 1;
    text-transform: uppercase;
}

.pill-value {
    color: var(--brand-color-3);
    font-size: 13px;
    font-weight: 600;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
    .dna-match-notification {
        max-width: 252px;
    }

    .notification-content {
        padding: 11px;
    }
}

@media (max-width: 480px) {
    .dna-match-notification {
        max-width: 90vw;
    }

    .notification-content {
        padding: 12px;
    }

    .match-person {
        font-size: 13px;
    }
}
</style>
